/**
 * The template catalogue served at `GET /catalogue/v1`. Mirrors
 * `catalogueSchema` in `src/platform/catalogue.ts` of the AI Workbench, which
 * writes entries into `.mojoup/templates/` without executing anything.
 */
import { z } from "zod";
import { deviceOrderRecordStateSchema, skillEvaluationResultSchema } from "./deviceOrders.js";
import { canonicalJson } from "./nodeManifest.js";
export const CATALOGUE_VERSION = 1;
/**
 * `mcp` is a tool server (tools.ts); its content is a `toolServerEntrySchema`. `document` is a
 * document template: its content is the template's Markdown, front matter included (`template`,
 * `summary`, `path: docs/{slug}.md`, optionally `okf: 1` and `type`); a workbench writes it to
 * `.mojoup/templates/documents/<id>.md`. Workbenches before 0.37 know a closed list of kinds, so
 * `GET /catalogue/v1` serves kinds beyond the first four only to a caller that sends `?include=`:
 * `include=mcp` gets tool servers, and any `include` gets document templates (a caller that asks
 * for something skips kinds it does not know).
 */
export const catalogueKindSchema = z.enum(["team", "role", "skill", "prompt", "mcp", "document", "agent", "instruction", "hook", "plugin", "marketplace", "design-system", "model", "evaluation"]);
/**
 * Customisations (D219, AIOE 0.19.0): what a workbench reads as agents, instructions, hooks,
 * plugins and marketplaces, published by the organisation beside its skills and prompts. Served
 * only to a caller whose `?include=` names the kind. `agent` content is Markdown with front matter
 * (`name`, `description`, optional `tools` string[], `model`: Claude Code's subagent format, which
 * the workbench writes for every runtime that reads agents). `instruction` content is Markdown,
 * optional front matter `appliesTo` (a glob). The other three carry objects below.
 */
export const CUSTOMISATION_KINDS = ["agent", "instruction", "hook", "plugin", "marketplace"];
export const customisationRuntimeSchema = z.enum(["claude-code", "copilot", "codex"]);
export const HOOK_EVENTS = ["before-tool", "after-tool", "after-tool-failure", "prompt-submitted", "agent-stopped", "subagent-stopped", "session-start", "session-end", "before-compact", "notification"];
export const hookEventSchema = z.enum(HOOK_EVENTS);
/** The event words the workbench shows; the console says the same. */
export const HOOK_EVENT_WORDS = {
    "before-tool": "Before a tool runs",
    "after-tool": "After a tool runs",
    "after-tool-failure": "After a tool fails",
    "prompt-submitted": "When you send a prompt",
    "agent-stopped": "When the agent stops",
    "subagent-stopped": "When a subagent stops",
    "session-start": "When a session starts",
    "session-end": "When a session ends",
    "before-compact": "Before the context is compacted",
    notification: "When a notification is sent",
};
/** `installed`: on every machine of the organisation, not editable there, told once in the inbox. `offered`: listed; the person adds it. */
export const customisationDeliverySchema = z.enum(["offered", "installed"]);
export const hookContentSchema = z
    .object({
    event: hookEventSchema,
    /** A tool-name pattern; only for the three tool events. */
    matcher: z.string().max(200).optional(),
    command: z.string().min(1).max(2000),
    /** Seconds. */
    timeout: z.number().int().min(1).max(3600).optional(),
    runtimes: z.array(customisationRuntimeSchema).min(1),
    delivery: customisationDeliverySchema,
})
    .refine((h) => !h.matcher || h.event === "before-tool" || h.event === "after-tool" || h.event === "after-tool-failure", { message: "a matcher applies to the tool events only" });
export const pluginContentSchema = z.object({
    /** The plugin's name in its marketplace. */
    plugin: z.string().min(1).max(200),
    /** The marketplace's name: a known public one, or a `marketplace` entry in the same catalogue (checked on publish). */
    marketplace: z.string().min(1).max(200),
    runtimes: z.array(z.enum(["claude-code", "copilot"])).min(1),
    delivery: customisationDeliverySchema,
});
export const marketplaceContentSchema = z.object({
    name: z.string().min(1).max(200),
    /** owner/repo, a git URL, or an https URL to marketplace.json. */
    source: z.string().min(1).max(500),
    runtimes: z.array(z.enum(["claude-code", "copilot"])).min(1),
});
/**
 * Design systems (AIOE 0.22.0, docs/plans/2026-10-design-systems.md): `design-system` entries carry
 * a `designSystemContentSchema` object (designSystems.ts) and their text in `files` (the tokens and
 * art stylesheets, references). Served only to a caller whose `?include=` names the kind, like the
 * D219 kinds, so the schema version does not change. A workbench turns one into a skill with
 * `designSystemSkill` (designSystems.ts).
 */
/**
 * Approved local models (AIOE 0.37.0, D257): `model` entries carry an `approvedModelSchema`
 * (deviceOrders.ts) as content. Served only to a caller whose `?include=` names the kind. On Mojo Up
 * AI Cloud publishing one requires `sha256`, and a model download is ordered from these only.
 */
export const MODEL_KIND = "model";
/*
 * Evaluation datasets (docs/evaluation.md): `evaluation` entries carry an `evaluationDatasetSchema`
 * (evaluation.ts) as content, parsed on publish. Served only to a caller whose `?include=` names the
 * kind; a node reads one for a `specialist.evaluate` order. Mojo Up ships none: an organisation
 * writes its own.
 */
export const DESIGN_SYSTEM_KIND = "design-system";
/** The kinds every workbench has always known; others are served only to a caller that asks with `?include=`. */
export const CATALOGUE_BASE_KINDS = ["team", "role", "skill", "prompt"];
/** Reference files on one catalogue entry, altogether. */
export const CATALOGUE_FILES_MAX_BYTES = 256 * 1024;
/** A relative path inside an entry's folder: forward slashes, no `..`, `.` or empty segments, no drive or leading slash. */
export const catalogueFilePathSchema = z
    .string()
    .min(1)
    .max(200)
    .refine((p) => !p.startsWith("/") && !p.includes("\\") && !/^[A-Za-z]:/.test(p) && !p.split("/").some((x) => x === ".." || x === "." || x === ""), "a relative path with forward slashes and no '..'");
/**
 * What an agent or a role is suited to: the set-up flow and first start choose agents by this.
 * Meaningful for kind `agent` and `role`; optional and additive, so a reader that does not know it ignores it.
 */
export const catalogueFitSchema = z.object({
    modelClass: z.enum(["fast", "standard", "strong"]).optional(),
    /** Project types it suits: "api", "web", "cli", "data", "mobile", "infrastructure". */
    suits: z.array(z.string().min(1).max(40)).max(20).optional(),
    /** Tool names the agent needs. */
    tools: z.array(z.string().min(1).max(200)).max(50).optional(),
    /**
     * AIOE 0.52.0 (workbench D272): the stacks an `instruction` or `skill` entry is for (CATALOGUE_STACKS); the
     * set-up flow includes it only in a repository whose detected stack matches. Left out: every repository.
     */
    stacks: z.array(z.string().min(1).max(40)).max(20).optional(),
});
/** The stacks a repository is detected as, the words `fit.stacks` uses. */
export const CATALOGUE_STACKS = ["npm", "pnpm", "yarn", "bun", "deno", "python", "pip", "poetry", "uv", "cargo", "go", "dotnet", "maven", "gradle", "ruby"];
/**
 * AIOE (Odysseus item 2, docs/plans/2026-10-odysseus-adoption.md): evidence on a skill, from a node's
 * `skill.evaluate` order (deviceOrders.ts). Set by the platform only: a submission or a publish that
 * carries it has it dropped. `contentDigest` is catalogueContentDigest of the skill evaluated; `stale`
 * is set when the entry's digest differs now or the organisation's default worker model
 * (`runtimes.defaultModel` in its settings policy) changed since. Nothing publishes on evidence.
 */
export const catalogueEvaluationSchema = z.object({
    at: z.string(),
    deviceId: z.string(),
    deviceName: z.string(),
    contentDigest: z.string().regex(/^[0-9a-f]{64}$/),
    worker: z.string(),
    judge: z.string(),
    result: skillEvaluationResultSchema,
    stale: z.literal(true).optional(),
});
/** AIOE, for the console: the newest `skill.evaluate` order for an entry or submission, while it is newer than the evidence. */
export const catalogueEvaluationOrderSchema = z.object({
    orderId: z.string(),
    deviceId: z.string(),
    deviceName: z.string(),
    issuedAt: z.string(),
    state: deviceOrderRecordStateSchema,
    /** The node's sentence when it failed or refused. */
    message: z.string().optional(),
});
/**
 * AIOE (Hermes item 1): what the platform's scanner found in an entry's content and files when it was submitted
 * or published. `dangerous` is not approved; `caution` is listed for the approver. Set by the platform.
 */
export const catalogueScanSchema = z.object({
    at: z.string(),
    level: z.enum(["clean", "caution", "dangerous"]),
    findings: z.array(z.object({ rule: z.string().max(60), words: z.string().max(300), where: z.string().max(220), line: z.number().int().positive().optional() })).max(50),
});
/**
 * AIOE (Hermes item 1): what a node found when it started a tool server entry and listed its tools, against what
 * the entry declares. `undeclared` not empty: the entry is not approved until the declaration names them.
 * `fingerprint` is the server definition the check ran on (tools.ts serverFingerprint); it is stale once that moves.
 */
export const catalogueVerificationSchema = z.object({
    at: z.string(),
    fingerprint: z.string().regex(/^[0-9a-f]{64}$/),
    deviceId: z.string(),
    deviceName: z.string(),
    tools: z.array(z.string().max(200)).max(500),
    undeclared: z.array(z.string().max(200)).max(500),
    missing: z.array(z.string().max(200)).max(500),
    sentence: z.string().max(500),
    stale: z.literal(true).optional(),
    /** D490: the server was reached and asks each person to sign in, so its tools are checked on first use; `tools` is empty and nothing is missing. */
    signIn: z.literal(true).optional(),
});
/** The digest evidence is kept against: lower-case hex SHA-256 of `{ content, files }` as canonical JSON (keys sorted; no files is `{}`). */
export async function catalogueContentDigest(entry) {
    const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(canonicalJson({ content: entry.content ?? null, files: entry.files ?? {} })));
    return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("");
}
export const catalogueEntrySchema = z.object({
    id: z.string().min(1).max(100),
    kind: catalogueKindSchema,
    name: z.string().min(1).max(200),
    summary: z.string().max(2000).optional(),
    version: z.string().max(40).optional(),
    /** Inline content: a TeamConfig object for teams, a tool server's definition for `mcp`, Markdown for the rest (a document template's with its front matter). */
    content: z.unknown(),
    /** Model class, project types and tools: for kinds `agent` and `role`; `stacks` for `instruction` and `skill` (see `catalogueFitSchema`). */
    fit: catalogueFitSchema.optional(),
    /**
     * AIOE 0.52.0 (workbench D272): for `skill`, `mcp` and `instruction` entries, as hooks and plugins carry it in
     * their content: `installed` puts it in every repository the set-up flow runs in (nodes apply it headless);
     * `offered` lists it for the person to choose. Left out: offered.
     */
    delivery: customisationDeliverySchema.optional(),
    /**
     * A skill's, a document template's or a design system's reference files beside its content, by relative path
     * (`tests.md`, `examples/ui.md`; forward slashes, no `..`), as UTF-8 text. At most
     * CATALOGUE_FILES_MAX_BYTES altogether. A workbench writes them next to the entry.
     */
    files: z
        .record(catalogueFilePathSchema, z.string())
        .refine((f) => Object.keys(f).length <= 100, "at most 100 files")
        .refine((f) => Object.entries(f).reduce((n, [k, v]) => n + new TextEncoder().encode(k).length + new TextEncoder().encode(v).length, 0) <= CATALOGUE_FILES_MAX_BYTES, "the files come to more than 256 KiB")
        .optional(),
    /** AIOE (Odysseus item 2): a skill's evidence, set by the platform (see catalogueEvaluationSchema). */
    evaluation: catalogueEvaluationSchema.optional(),
    /** AIOE: `skill_load`s workbenches reported for this entry in the last 90 days (`POST /catalogue/v1/usage`), and the last hour one was. Served to signed-in people. */
    loads90d: z.number().int().nonnegative().optional(),
    lastLoadedAt: z.string().optional(),
    /** AIOE, for the console: an evaluation asked for and not yet in (or one that did not run). */
    evaluating: catalogueEvaluationOrderSchema.optional(),
    /**
     * AIOE (Hermes item 6): an archived entry is kept and restorable but served to no workbench; `pinned` keeps a
     * skill from being offered for archiving. Both set by an administrator (`PATCH /admin/v1/catalogue/:id/standing`).
     */
    status: z.enum(["published", "archived"]).optional(),
    pinned: z.boolean().optional(),
    /** AIOE: when the entry was last published or changed, and the teams whose members name a skill (set by the platform). */
    changedAt: z.string().optional(),
    usedByTeams: z.array(z.string().max(200)).max(50).optional(),
    /** AIOE (Hermes item 1): the scanner's findings, and a tool server's verification on a node, set by the platform. */
    scan: catalogueScanSchema.optional(),
    verified: catalogueVerificationSchema.optional(),
    verifying: catalogueEvaluationOrderSchema.optional(),
});
/** What the platform sets on an entry and never takes from a caller. */
export const CATALOGUE_PLATFORM_FIELDS = ["evaluation", "loads90d", "lastLoadedAt", "evaluating", "scan", "verified", "verifying", "changedAt", "usedByTeams"];
/** AIOE (Hermes item 6): a skill not loaded for this long (and not changed for as long) is offered for archiving. */
export const CATALOGUE_UNUSED_DAYS = 30;
/** `PATCH /admin/v1/catalogue/:id/standing`. */
export const catalogueStandingSchema = z.object({ status: z.enum(["published", "archived"]).optional(), pinned: z.boolean().optional() }).refine((s) => s.status !== undefined || s.pinned !== undefined, "say what changes: status or pinned");
/**
 * Whether a skill is offered for archiving: not loaded for CATALOGUE_UNUSED_DAYS (and, when it was ever loaded, not
 * since), not changed for as long, not pinned, named by no team, and not archived already. `nowIso` is now.
 */
export function unusedSkill(e, nowIso) {
    if (e.kind !== "skill" || e.status === "archived" || e.pinned || e.usedByTeams?.length || e.loads90d === undefined || !e.changedAt)
        return false;
    const cut = Date.parse(nowIso) - CATALOGUE_UNUSED_DAYS * 86_400_000;
    return Date.parse(e.changedAt) < cut && (e.lastLoadedAt ? Date.parse(e.lastLoadedAt) < cut : true);
}
/**
 * AIOE (Odysseus item 2): `POST /catalogue/v1/usage` with a device token, beside the hourly tool
 * counts: `skill_load`s per catalogue entry by the hour (UTC, ISO, on the hour; a time inside the
 * hour is put on it). The same machine, entry and hour sent again replaces the count. Kept 90 days.
 */
export const CATALOGUE_USAGE_DAYS = 90;
export const catalogueUsageSchema = z.object({
    loads: z
        .array(z.object({ entryId: z.string().min(1).max(100), hour: z.string().min(13).max(30), loads: z.number().int().nonnegative().max(10_000_000) }))
        .max(2000),
});
/**
 * AIOE (Odysseus item 2): what an administrator asks for.
 *
 *   POST /admin/v1/catalogue/:id/evaluate               a published skill -> 201 DeviceOrderRecord (kind skill.evaluate)
 *   POST /admin/v1/catalogue/submissions/:id/evaluate   a pending skill submission -> 201 DeviceOrderRecord
 *
 * `deviceId` is an approved node. `tasks` (at most five) are what the skill is for; left out, the
 * submitter's `tests.md` gives them, else the node's judge writes them from the summary. `unrelated`
 * (at most two) are asks the skill must not be loaded for; left out, the platform picks two.
 */
export const catalogueEvaluateRequestSchema = z.object({
    deviceId: z.string().min(1).max(100),
    tasks: z
        .array(z.union([z.string().trim().min(1).max(2000), z.object({ prompt: z.string().trim().min(1).max(2000), expect: z.string().trim().max(500).optional() })]))
        .max(5)
        .optional(),
    worker: z.string().trim().min(1).max(200).optional(),
    judge: z.string().trim().min(1).max(200).optional(),
    unrelated: z.array(z.string().trim().min(1).max(2000)).max(2).optional(),
});
/**
 * AIOE (Odysseus item 3): where a submission came from, when a workbench drafted it from real work.
 * `drafted`: a model drafted it from a task (the console says "Drafted from T-14 on Dana's
 * workbench"). With a device token the platform sets `deviceId` to that device.
 */
export const catalogueSubmissionOriginSchema = z.object({
    runId: z.string().min(1).max(100).optional(),
    taskId: z.string().min(1).max(100).optional(),
    deviceId: z.string().min(1).max(100).optional(),
    drafted: z.literal(true).optional(),
    /** Made with the specialist designer in a conversation (workbench D272). */
    designed: z.literal(true).optional(),
});
export const catalogueSchema = z.object({
    version: z.literal(CATALOGUE_VERSION),
    publishedAt: z.string().optional(),
    entries: z.array(catalogueEntrySchema).max(500),
});
/**
 * Publishing from a workbench (D219): `POST /catalogue/v1/submissions` with a device token (for
 * its person, or `x-aioe-principal` as on the board) or a sign-in. An administrator's, or anyone's
 * when the organisation lets members publish, is published at once; otherwise it waits for an
 * administrator in the console, who approves or declines with a reason.
 *
 *   POST /catalogue/v1/submissions            { entry (no id), replaces? } -> { state: "published", id } | { state: "pending", submissionId }
 *   GET  /catalogue/v1/submissions/mine       the caller's submissions and their state
 *   GET  /admin/v1/catalogue/submissions      pending ones; POST …/:id/approve, POST …/:id/decline { reason }
 *   403 not_allowed in words when the organisation turned submissions off (`catalogue.submissions` setting).
 */
export const catalogueSubmissionBodySchema = z.object({
    entry: catalogueEntrySchema.omit({ id: true, evaluation: true, loads90d: true, lastLoadedAt: true, evaluating: true }),
    /** The id of the entry this one replaces, when it is a new version of one. */
    replaces: z.string().min(1).max(100).optional(),
    /** AIOE (Odysseus item 3): where it came from. */
    origin: catalogueSubmissionOriginSchema.optional(),
});
export const catalogueSubmissionStateSchema = z.enum(["pending", "published", "declined"]);
/** Who may publish without approval: administrators always; `members` lets anyone in the organisation. `off` refuses submissions. */
export const catalogueSubmissionsSettingSchema = z.enum(["admins", "members", "off"]);
//# sourceMappingURL=catalogue.js.map